import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "../../api/client";
import { useInHouse } from "../../context/inHousePermissions";
import { hasElevatedRoute } from "../../utils/roleAccess";
// Переиспользуем стили продаж (.tbl, .modal, .btn, .field, .form, .msg).
import "../sales/index.css";

interface DueRow {
  userId: number;
  username: string | null;
  status: string;
  currency: string;
  fullAmount: number | null;
  paid: number;
  firstTxCurrency: string;
}

// Счета — значения bank enum из backend schema.ts (как на /transactions).
const BANKS = [
  "kaspi",
  "kaspi_gold",
  "kaspi_pay",
  "vtb",
  "tinkoff",
  "optima",
  "halyk",
  "stripe",
  "cash",
  "other",
];

const CURRENCIES = ["KZT", "USD", "AZN", "BYN", "GEL", "TRY", "RUB", "EUR", "KGS"];

// Доступ к таблице — только admin/operations.
const ALL_SEERS = ["admin", "operations"];

const fmt = (n: number) => n.toLocaleString("ru-RU");

// Ключ виртуальной папки «Отложка» — тот же, что в storage/index.tsx.
const POSTPONED_KEY = "postponed";

// YYYY-MM-DD → DD.MM.YY, как подписаны дате-папки в хранилище.
const folderLabel = (key: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  return m ? `${m[3]}.${m[2]}.${m[1].slice(2)}` : key;
};

interface PostpaymentDueProps {
  // Если задан — показываем только этих юзеров (напр. за выбранный складской день).
  filterUserIds?: number[];
  /** userId → папка хранилища (складской день либо "postponed"). Даёт колонку
   *  с датой папки и убирает из таблицы книги из отложки: с них постоплату
   *  пока не собирают. Ключи строковые — Map<number> в JSX неудобен. */
  folderByUserId?: Map<string, string>;
  /** Корень хранилища цеха для ссылок на папку (/storage/almaty). */
  basePath: string;
}

const PostpaymentDue: React.FC<PostpaymentDueProps> = ({
  filterUserIds,
  folderByUserId,
  basePath,
}) => {
  const { currentUserRecord } = useInHouse();
  const role = currentUserRecord?.role || "";
  // Персонально выданные «Транзакции» = права all-seer, как на самой
  // странице транзакций и на бэке (utils/staffAccess.ts).
  const canSee =
    ALL_SEERS.includes(role) ||
    hasElevatedRoute(currentUserRecord?.extraRoutes, "/transactions");

  const [rows, setRows] = useState<DueRow[]>([]);
  const [loading, setLoading] = useState(true);

  // Попап «Собрать оплату».
  const [target, setTarget] = useState<DueRow | null>(null);
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("KZT");
  const [bank, setBank] = useState("kaspi");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    if (!canSee) {
      setLoading(false);
      return;
    }
    client
      .get<DueRow[]>("/transactions/postpayment-due")
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch((err) => console.error("[storage] postpayment-due fetch failed", err))
      .finally(() => setLoading(false));
  }, [canSee]);

  if (!canSee) return null;

  // На странице дня ограничиваем список юзерами этого дня.
  const dayRows = filterUserIds
    ? rows.filter((r) => filterUserIds.includes(r.userId))
    : rows;
  // Книги из отложки не показываем: их ещё не собирают и не печатают.
  const visibleRows = folderByUserId
    ? dayRows.filter((r) => folderByUserId.get(String(r.userId)) !== POSTPONED_KEY)
    : dayRows;

  const openCollect = (row: DueRow) => {
    setTarget(row);
    setAmount("");
    // Валюта по умолчанию — как в первой транзакции этого юзера.
    setCurrency(row.firstTxCurrency || row.currency || "KZT");
    setBank("kaspi");
    setMsg(null);
  };

  const handleCollect = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!target) return;
    setSaving(true);
    setMsg(null);
    try {
      await client.post("/transactions", {
        userId: target.userId,
        amount: Number(amount) || 0,
        currency,
        bank,
        paymentType: "postpayment",
      });
      // Полная доплата → юзер выпадает из списка; частичная — остаётся
      // с новым «Оплачено», поэтому перечитываем список с бэка.
      const r = await client.get<DueRow[]>("/transactions/postpayment-due");
      setRows(Array.isArray(r.data) ? r.data : []);
      setTarget(null);
    } catch (err: any) {
      setMsg({ ok: false, text: err.response?.data?.error || "Ошибка" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section style={{ marginTop: 48 }}>
      <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
        Сбор постоплаты{" "}
        <span style={{ color: "var(--text-3)", fontWeight: 400 }}>
          · {visibleRows.length}
        </span>
      </h3>

      {loading ? (
        <div style={{ color: "var(--text-3)" }}>Загрузка…</div>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>Юзер</th>
              <th>Папка</th>
              <th>Оплачено</th>
              <th>Статус</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((r) => (
              <tr key={r.userId}>
                <td>{r.username || `userId ${r.userId}`}</td>
                <td>
                  {(() => {
                    const key = folderByUserId?.get(String(r.userId));
                    if (!key) return <span style={{ color: "var(--text-3)" }}>—</span>;
                    return <Link to={`${basePath}/${key}`}>{folderLabel(key)}</Link>;
                  })()}
                </td>
                <td>
                  {fmt(r.paid)} / {r.fullAmount != null ? fmt(r.fullAmount) : "—"}{" "}
                  {r.currency}
                </td>
                <td>{r.status}</td>
                <td style={{ textAlign: "right" }}>
                  <button className="btn" onClick={() => openCollect(r)}>
                    Собрать оплату
                  </button>
                </td>
              </tr>
            ))}
            {visibleRows.length === 0 && (
              <tr>
                <td colSpan={5} style={{ textAlign: "center", color: "var(--text-3)" }}>
                  Некого собирать — все оплаты закрыты
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {target && (
        <div className="modal-overlay" onClick={() => setTarget(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>
                Собрать оплату · {target.username || `userId ${target.userId}`}
              </h3>
              <button
                className="modal-close"
                onClick={() => setTarget(null)}
                aria-label="Закрыть"
              >
                ×
              </button>
            </div>

            {msg && (
              <div className={`msg ${msg.ok ? "msg--ok" : "msg--err"}`}>
                {msg.text}
              </div>
            )}

            <form className="form" onSubmit={handleCollect}>
              <div className="field">
                <label>Тип транзакции</label>
                <input value="Постоплата" readOnly />
              </div>

              <div className="form-row">
                <div className="field">
                  <label>Сумма</label>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0"
                    autoFocus
                  />
                </div>
                <div className="field">
                  <label>Валюта</label>
                  <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
                    {CURRENCIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="field">
                <label>Счет</label>
                <select value={bank} onChange={(e) => setBank(e.target.value)}>
                  {BANKS.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </div>

              <button type="submit" className="btn" disabled={saving}>
                {saving ? "Сохранение…" : "Собрать оплату"}
              </button>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default PostpaymentDue;
